import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import {
  ColorOptionDto,
  QuantityCollapsibleDetailsDto,
  QuantityCollapsibleStepDto,
  QuantityPerColorCollapsibleStepDto,
  QuantityPerSizeCollapsibleStepDto,
  QuantityPerSizePerColorCollapsibleStepDto,
  StepEnum,
} from '@/api-sdk';
import { FocusManagementMixin } from '@/components/configurator/focus-management-step-mixin';
import { baseStyles, buttonStyles } from '@/styles';
import { QuantityCollapsibleData, QuantityData } from '@/types';
import { findFirstFocusableRecursive } from '@/utils';

import '@/components/configurator/step/quantity/fg-quantity-per-color-table';
import '@/components/configurator/step/quantity/fg-quantity-per-size-per-color-table';
import '@/components/configurator/step/quantity/fg-quantity-per-size-table';
import '@/components/configurator/step/quantity/fg-quantity-table';

@customElement('fg-collapsible-quantity-wrapper')
@localized()
export class FgCollapsibleQuantityWrapper extends FocusManagementMixin(
  LitElement,
) {
  private quantityTableContainerRef: Ref<HTMLDivElement> = createRef();
  @property({ type: Number })
  index!: number;

  @property({ type: Boolean })
  open = false;

  @property({ type: Boolean })
  valid = false;

  @property({ type: Object })
  details!: QuantityCollapsibleDetailsDto;

  @property({ type: Boolean })
  loading = false;

  @property({ type: Object })
  quantityData?: QuantityData | QuantityCollapsibleData;

  @property({ type: String })
  quantityStep?: StepEnum;

  @property({ type: Array })
  colors: ColorOptionDto[] = [];

  protected willUpdate(_changedProperties: PropertyValues) {
    if (_changedProperties.has('open') && this.open) {
      this.showGoToNextStepButton = true;
    }

    super.willUpdate(_changedProperties);
  }

  renderA11yDescription() {
    const quantityData = this.quantityData as QuantityCollapsibleData;
    return `
        ${quantityData?.details?.minimumQuantityDescription ?? ''}
        ${quantityData?.details?.maximumHspQuantityDescription ?? ''}
        ${t('labels.pricesExclVat')}
    `;
  }

  /**
   * Focuses the first focusable element in the quantity table container
   */
  async focusFirstQuantityElement() {
    this.updateComplete.then(async () => {
      if (this.quantityTableContainerRef?.value === undefined) return;
      const focusableElement = await findFirstFocusableRecursive(
        this.quantityTableContainerRef?.value,
      );
      focusableElement?.focus();
    });
  }

  /**
   * Override the focusFirstOptionElement method to call focusFirstQuantityElement
   */
  async focusFirstOptionElement() {
    await this.focusFirstQuantityElement();
  }

  renderQuantitiesTable() {
    switch (this.quantityStep) {
      case StepEnum.quantity_per_color_collapsible: {
        const quantityData = this
          .quantityData as QuantityPerColorCollapsibleStepDto;
        return html`
          <fg-quantity-per-color-table
            .options=${quantityData?.options}
            .minimumQuantityDescription=${quantityData?.details
              .minimumQuantityDescription}
            .totalQuantityDescription=${quantityData?.details
              .totalQuantityDescription}
            .hspMaximumQuantity=${quantityData?.details
              .maximumHspQuantityDescription}
            .totalPrice=${quantityData?.details.totalPrice}
            .collapsible=${true}
            .loading=${this.loading}
            .a11yDescription=${this.renderA11yDescription()}
          ></fg-quantity-per-color-table>
        `;
      }

      case StepEnum.quantity_per_size_collapsible: {
        const quantityData = this
          .quantityData as QuantityPerSizeCollapsibleStepDto;
        return html`
          <fg-quantity-per-size-table
            .options=${quantityData?.options}
            .totalQuantityDescription=${quantityData?.details
              .totalQuantityDescription}
            .hspMaximumQuantity=${quantityData?.details
              .maximumHspQuantityDescription}
            .totalPrice=${quantityData?.details.totalPrice}
            .loading=${this.loading}
            .a11yDescription=${this.renderA11yDescription()}
          ></fg-quantity-per-size-table>
        `;
      }
      case StepEnum.quantity_per_size_per_color_collapsible: {
        const quantityData = this
          .quantityData as QuantityPerSizePerColorCollapsibleStepDto;
        return html`
          <fg-quantity-per-size-per-color-table
            .colors=${this.colors}
            .options=${quantityData?.options}
            .totalQuantityDescription=${quantityData?.details
              .totalQuantityDescription}
            .hspMaximumQuantity=${quantityData?.details
              .maximumHspQuantityDescription}
            .totalPrice=${quantityData?.details.totalPrice}
            .totalQuantity=${quantityData?.details.selectedQuantity}
            .loading=${this.loading}
            .a11yDescription=${this.renderA11yDescription()}
          ></fg-quantity-per-size-per-color-table>
        `;
      }
      case StepEnum.quantity_collapsible: {
        const quantityData = this.quantityData as QuantityCollapsibleStepDto;

        return html`
          <fg-quantity-table
            .options=${quantityData?.options?.tierOptions}
            .loading=${this.loading}
            .customQuantityOption=${quantityData?.options?.customOption}
            .collapsible=${true}
            .a11yDescription=${this.renderA11yDescription()}
          ></fg-quantity-table>
        `;
      }
    }
  }

  render() {
    const quantityData = this.quantityData as QuantityCollapsibleData;
    return html`
      <div
        id="sect_${this.index}"
        role="region"
        aria-labelledby="accordion_${this.index}"
        ?hidden=${!this.open}
        ref=${ref(this.quantityTableContainerRef)}
      >
        ${this.renderNextStepButton(quantityData?.details?.title)}
        <p class="minimum-quantity-text">
          ${quantityData?.details?.minimumQuantityDescription}
          ${quantityData?.details?.maximumHspQuantityDescription}
        </p>
        ${this.renderQuantitiesTable()}
        <p class="excluding-tax-text">${t('labels.pricesExclVat')}</p>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      fieldset {
        border: none;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        margin-top: 16px;
        padding: 16px;
      }

      fieldset.selected {
        background-color: var(
          --fg-tertiary--light,
          var(--fg-fallback-tertiary--light)
        );
        border: 1px solid var(--fg-info, var(--fg-fallback-info));
      }

      .minimum-quantity-text,
      .excluding-tax-text {
        font-style: italic;
      }

      .minimum-quantity-text {
        margin-bottom: 16px;
        margin-top: 8px;
      }

      .excluding-tax-text {
        margin-top: 16px;
        display: block;
        text-align: right;
        width: 100%;
      }
    `,
  ];
}
